import { type Reaction } from "@/data/reactions";
import { motion } from "framer-motion";
import { Flame, Snowflake, Beaker, Zap, Scale, Calculator, ArrowDownToLine, ArrowUpFromLine, ListOrdered } from "@/lib/icons";
import { Link } from "react-router-dom";
import EnergyDiagram from "./EnergyDiagram";

// Convert unicode subscripts/superscripts in equations to plain ASCII
// so the Equation Balancer and Molar Mass tools can parse them.
const SUB_MAP: Record<string, string> = {
  "₀": "0", "₁": "1", "₂": "2", "₃": "3", "₄": "4",
  "₅": "5", "₆": "6", "₇": "7", "₈": "8", "₉": "9",
};
const SUP_MAP: Record<string, string> = {
  "⁰": "0", "¹": "1", "²": "2", "³": "3", "⁴": "4",
  "⁵": "5", "⁶": "6", "⁷": "7", "⁸": "8", "⁹": "9",
  "⁺": "+", "⁻": "-",
};
function toAscii(s: string): string {
  return s
    .split("")
    .map((c) => SUB_MAP[c] ?? SUP_MAP[c] ?? c)
    .join("")
    .replace(/→/g, "->")
    .replace(/⇌/g, "->");
}

interface ReactionResultProps {
  reaction: Reaction;
}

export default function ReactionResult({ reaction }: ReactionResultProps) {
  const asciiEquation = toAscii(reaction.equation);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
      className="w-full max-w-2xl mx-auto space-y-4"
    >
      <div className="rounded-xl border border-border bg-card p-5 space-y-4 shadow-lg">
        <div className="text-center">
          <p className="font-mono text-lg font-semibold text-foreground tracking-wide">
            {reaction.equation}
          </p>
        </div>

        <div className="flex items-center justify-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
            <Beaker size={12} />
            {reaction.typeLabel}
          </span>
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
              reaction.energy === "exothermic"
                ? "bg-destructive/10 text-destructive border-destructive/20"
                : "bg-blue-500/10 text-blue-400 border-blue-400/20"
            }`}
            aria-label={
              reaction.energy === "exothermic"
                ? "Exothermic reaction - releases heat"
                : "Endothermic reaction - absorbs heat"
            }
          >
            {reaction.energy === "exothermic" ? (
              <>
                <Flame size={12} aria-hidden="true" />
                <ArrowUpFromLine size={10} aria-hidden="true" />
                Exothermic (releases heat)
              </>
            ) : (
              <>
                <Snowflake size={12} aria-hidden="true" />
                <ArrowDownToLine size={10} aria-hidden="true" />
                Endothermic (absorbs heat)
              </>
            )}
          </span>
          {reaction.orderMatters && (
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border bg-yellow-500/10 text-yellow-500 border-yellow-500/20"
              aria-label="Order of addition matters for this reaction"
            >
              <ListOrdered size={12} aria-hidden="true" />
              Order matters
            </span>
          )}
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed">{reaction.description}</p>

        {reaction.orderMatters && reaction.orderHint && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/5 border border-yellow-500/20">
            <ListOrdered size={14} className="text-yellow-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <div>
              <p className="text-xs font-medium text-foreground mb-0.5">Procedure note</p>
              <p className="text-xs text-muted-foreground">{reaction.orderHint}</p>
            </div>
          </div>
        )}

        <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 border border-border">
          <Zap size={14} className="text-yellow-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-foreground mb-0.5">What you'd observe:</p>
            <p className="text-xs text-muted-foreground">{reaction.observation}</p>
          </div>
        </div>

        <div>
          <p className="text-xs text-muted-foreground mb-2">Products formed:</p>
          <div className="flex items-center gap-2 flex-wrap">
            {reaction.products.map((product, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-mono font-medium bg-accent text-foreground border border-border"
              >
                {product}
              </span>
            ))}
          </div>
        </div>

        {/* Cross-tool deep links */}
        <div className="pt-2 border-t border-border">
          <p className="text-[11px] text-muted-foreground mb-2">Continue in another tool:</p>
          <div className="flex flex-wrap gap-2">
            <Link
              to={`/tools/equation-balancer?eq=${encodeURIComponent(asciiEquation)}`}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium bg-primary/10 text-primary border border-primary/20 hover:bg-primary/15 transition-colors"
              aria-label={`Open this equation in the Equation Balancer`}
            >
              <Scale size={12} aria-hidden="true" />
              Balance equation
            </Link>
            {reaction.products.map((product, i) => (
              <Link
                key={i}
                to={`/tools/molar-mass?formula=${encodeURIComponent(toAscii(product))}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium bg-accent text-foreground border border-border hover:bg-accent/70 transition-colors font-mono"
                aria-label={`Calculate molar mass of ${product}`}
              >
                <Calculator size={12} aria-hidden="true" />
                M({product})
              </Link>
            ))}
          </div>
        </div>
      </div>

      <EnergyDiagram reaction={reaction} />
    </motion.div>
  );
}
